import { getField, updateField } from 'vuex-map-fields';
import { mapObjIndexed, pathOr } from 'ramda';
import { getFormVisibilities, getEetFormConfig, getFormValidations } from '~/config/formConfig';

export const CERT_PWD_ERROR = 'Chybné heslo certifikátu';

/* eslint no-underscore-dangle: "off" */

const defaultState = {
  visibilities: {},
  establishment_number: '',
  cert_password: '',
  vat_number: '',
  testing_mode: '',
  enabled: '',
  cert_file: [],
  not_subject_to_eet: '',
  dialog_opened: false,
  api_validation: '',
  errorMessages: {},
};

const mutations = {
  updateField,
  updateStoreValue(state, data) {
    state[data.name] = data.value;
  },
  computeVisibilities(state, { rootState }) {
    state.visibilities = getFormVisibilities(rootState, 'eetForm');
  },
  openEetDialog(state) {
    state.dialog_opened = true;
  },
  clearErrorMessages(state) {
    state.errorMessages = {};
  },
  getSettingsLoaded(state, data) {
    state.establishment_number = data.establishment_number;
    state.cert_password = data.cert_password;
    state.vat_number = data.vat_number;
    state.testing_mode = data.testing_mode;
    state.enabled = data.enabled;
    state.not_subject_to_eet = data.not_subject_to_eet;

    if (pathOr(false, ['cert_file', 'url'], data)) {
      state.cert_file = [
        {
          url: data.cert_file.url,
          name: data.cert_file.filename,
        },
      ];
    }
  },
  getSettingsLoadError(state) {
    state.api_validation = 'eetForm.errors.api_validation';
  },
  settingsStored: (state, action) => {
    if (action.payload.data.id) {
      state.api_validation = '';
      state.dialog_opened = false;
      return;
    }
    state.api_validation = 'eetForm.errors.server_validation_error';
  },
  settingsStoreError: (state, action) => {
    // 500 error
    if (action.payload.response.status === 500) {
      state.api_validation = 'eetForm.errors.server_error';
      return;
    }
    // general error
    if (action.payload.response.data.message) {
      state.api_validation = action.payload.response.data.message;
      return;
    }
    if (action.payload.response.data.error) {
      // field validations errors
      const response = JSON.parse(action.payload.request.response);
      state.errorMessages = {
        ...state.errorMessages,
        ...mapObjIndexed(err => err.join(', '), response.error),
      };
      // other known errors
      if (action.payload.response.data.error.base) {
        if (action.payload.response.data.error.base.find(item => item === CERT_PWD_ERROR)) {
          state.errorMessages = {
            ...state.errorMessages,
            cert_password: CERT_PWD_ERROR,
          };
          return;
        }
        state.api_validation = action.payload.response.data.error.base.join(', ');
      }
    }
  },
  fileUploadFailed: (state) => {
    state.errorMessages = {
      ...state.errorMessages,
      cert_file: 'eetForm.errors.upload_fail',
    };
  },
};

const actions = {
  settingsStoreError: ({ commit }, data) => {
    commit('settingsStoreError', data);
  },
  settingsStored: ({ commit }, action) => {
    commit('settingsStored', action);
    if (action.payload.data.id) {
      let eetEnabled = action.payload.data.enabled;
      let eetVisible = true;

      if (action.payload.data.not_subject_to_eet === true) {
        eetEnabled = false;
        eetVisible = false;
      }

      commit('config/changeEetStatus', eetEnabled, { root: true });
      commit('config/changeEetVisibility', eetVisible, { root: true });
    }
  },
  getSettingsLoaded: ({ commit }, action) => {
    commit('getSettingsLoaded', action.payload.data);
  },
  getSettingsLoadError: ({ commit }, action) => {
    commit('getSettingsLoadError', action);
  },
  updateStoreValue: ({ commit }, action) => {
    commit('updateStoreValue', action);
  },
  computeVisibilities: ({ commit, rootState }) => {
    commit('computeVisibilities', { rootState });
  },
  clearErrorMessages: ({ commit }) => {
    commit('clearErrorMessages');
  },
  openEetDialog: ({ commit }) => {
    commit('openEetDialog');
  },
  storeSettings({ commit, state, rootGetters }) {
    if (!state.not_subject_to_eet && (!state.cert_file || state.cert_file.length === 0)) {
      commit('updateStoreValue', {
        name: 'api_validation',
        value: 'eetForm.errors.cert_missing',
      });
      return;
    }
    commit('updateStoreValue', {
      name: 'api_validation',
      value: '',
    });

    const certFile = pathOr(false, ['cert_file', [0], 'raw'], state) && {
      cert_file_id: state.cert_file[0].response.id,
    };

    commit(
      'api/fetch',
      {
        url: '/eet-settings',
        method: certFile ? 'PUT' : 'PATCH',
        headers: {
          ...rootGetters['auth/getHeaders'],
        },
        onSuccess: ['eetForm/settingsStored'],
        onError: ['eetForm/settingsStoreError'],
        body: {
          eet_setting: {
            establishment_number: state.establishment_number,
            cert_password: state.cert_password,
            vat_number: state.vat_number,
            enabled: state.enabled,
            testing_mode: state.testing_mode,
            not_subject_to_eet: state.not_subject_to_eet,
            ...certFile,
          },
        },
      },
      { root: true },
    );
  },
  getSettings({ commit, rootGetters }) {
    commit(
      'api/fetch',
      {
        url: '/eet-settings/edit',
        method: 'GET',
        headers: {
          ...rootGetters['auth/getHeaders'],
        },
        onSuccess: ['eetForm/getSettingsLoaded'],
        onError: ['eetForm/getSettingsLoadError'],
      },
      { root: true },
    );
  },
};

const getters = {
  getField,
  eet: state => state,
  validationRules: state => getFormValidations(getEetFormConfig(state)),
  visibilities: state => state.visibilities,
};

export default {
  namespaced: true,
  state: defaultState,
  getters,
  actions,
  mutations,
};
